// @flow

/**
 * Scroll Service Utilities
 * Centralized utility functions for scroll calculations and operations
 */

/**
 * Check if a container is scrolled to the bottom within a threshold
 * @param {HTMLElement} container - The scrollable container element
 * @param {number} threshold - Distance in pixels to consider "at bottom" (default 10)
 * @returns {boolean} True if the container is at or near the bottom
 */
export const isAtBottom = (container, threshold = 10) => {
  if (!container) return false;
  const { scrollTop, scrollHeight, clientHeight } = container;
  return scrollTop + clientHeight >= scrollHeight - threshold;
};

/**
 * Scroll an element into view with options
 * @param {HTMLElement} element - The element to scroll into view
 * @param {Object} options - Scroll options
 * @param {string} options.behavior - 'auto' or 'smooth' (default 'smooth')
 * @param {string} options.block - 'start', 'center', 'end', 'nearest' (default 'end')
 * @param {string} options.inline - 'start', 'center', 'end', 'nearest' (default 'nearest')
 * @returns {void}
 */
export const scrollToElement = (element, options = {}) => {
  if (!element) return;
  
  const {
    behavior = 'smooth',
    block = 'end',
    inline = 'nearest',
  } = options;

  // Use requestAnimationFrame to ensure DOM has updated
  requestAnimationFrame(() => {
    if (element) {
      element.scrollIntoView({
        behavior,
        block,
        inline,
      });
    }
  });
};

/**
 * Scroll a container to the bottom
 * @param {HTMLElement} container - The scrollable container element
 * @param {string} behavior - 'auto' or 'smooth' (default 'auto')
 * @returns {void}
 */
export const scrollToBottom = (container, behavior = 'auto') => {
  if (!container) return;
  
  requestAnimationFrame(() => {
    if (container) {
      if (behavior === 'smooth') {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }
  });
};

/**
 * Check if an element is visible within its container's viewport
 * @param {HTMLElement} element - The element to check
 * @param {HTMLElement} container - The container element
 * @returns {boolean} True if the element is at least partially visible
 */
export const isElementVisible = (element, container) => {
  if (!element || !container) return false;

  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return (
    elementRect.top >= containerRect.top &&
    elementRect.bottom <= containerRect.bottom &&
    elementRect.left >= containerRect.left &&
    elementRect.right <= containerRect.right
  );
};
